import React, { useState } from 'react';
import { IconButton } from '@atlaskit/button/new';
import EditIcon from '@atlaskit/icon/core/edit';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import { ProjectIcon } from '@atlassian/jira-horizontal-nav-icons/src/ui/icon/index.tsx';
import messages from './messages.tsx';
import type { EditableAvatarProps } from './types.tsx';

const EditIconWithTransparentBackground = () => (
	<Box xcss={iconBackgroundStyles}>
		<EditIcon label="" color={token('color.icon.inverse')} />
	</Box>
);

const ProjectAvatar = ({ src, testId }: { src: string; testId: string }) => (
	<ProjectIcon
		data-testid={testId && `${testId}--image`}
		data-vc="editable-avatar-project"
		src={src}
		alt=""
	/>
);

export const EditableAvatar = (props: EditableAvatarProps): React.JSX.Element => {
	const editIconLabelGalaxia = expVal('project-terminology-refresh', 'isEnabled', false)
		? messages.editIconLabelGalaxia
		: messages.editIconLabel;
	const { avatarUrl, canEdit, triggerProps = {}, onClick } = props;
	const [isHover, setIsHover] = useState(false);
	const { formatMessage } = useIntl();

	return (
		<Box onMouseEnter={() => setIsHover(true)} onMouseLeave={() => setIsHover(false)}>
			{canEdit ? (
				<Tooltip content={formatMessage(editIconLabelGalaxia)} position="bottom">
					{(tooltipProps) => {
						return (
							<Box xcss={editableAvatarContainerStyles}>
								{isHover && (
									<Box
										xcss={iconButtonWrapperStyles}
										// eslint-disable-next-line react/jsx-props-no-spreading
										{...tooltipProps}
									>
										<IconButton
											appearance="subtle"
											icon={EditIconWithTransparentBackground}
											label={formatMessage(editIconLabelGalaxia)}
											spacing="compact"
											{...triggerProps}
											onClick={onClick}
										/>
									</Box>
								)}
								<ProjectAvatar
									src={avatarUrl ? `${avatarUrl}?size=xlarge` : avatarUrl}
									testId="navigation-apps-sidebar-inline-config-project-header.ui.editable-avatar.project-icon-editable"
								/>
							</Box>
						);
					}}
				</Tooltip>
			) : (
				<ProjectAvatar
					src={avatarUrl ? `${avatarUrl}?size=xlarge` : avatarUrl}
					testId="navigation-apps-sidebar-inline-config-project-header.ui.editable-avatar.project-icon"
				/>
			)}
		</Box>
	);
};

const iconBackgroundStyles = xcss({
	backgroundColor: 'color.background.neutral.pressed',
	borderRadius: 'radius.xsmall',
});

const editableAvatarContainerStyles = xcss({ position: 'relative' });

const iconButtonWrapperStyles = xcss({
	position: 'absolute',
	zIndex: 'card',
	left: token('space.negative.025'),
	top: token('space.negative.025'),
});
